import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { Page } from '#/shared/api/contract'

export function useRecordNeighbours({
  resourceKey,
  currentId,
  onNavigate,
}: {
  resourceKey: readonly unknown[]
  currentId: string
  onNavigate: (id: string) => void
}): { onPrev?: () => void; onNext?: () => void; position?: string } {
  const queryClient = useQueryClient()

  const ids = useMemo(() => {
    const entries = queryClient.getQueriesData<Page<{ id: string }>>({
      queryKey: [...resourceKey, 'list'],
    })
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const page = entries[index]?.[1]
      const rows = page?.items
      if (rows?.some((row) => row.id === currentId)) return rows.map((row) => row.id)
    }
    return []
  }, [queryClient, resourceKey, currentId])

  const at = ids.indexOf(currentId)
  if (at < 0) return {}

  const previous = at > 0 ? ids[at - 1] : undefined
  const next = at < ids.length - 1 ? ids[at + 1] : undefined

  return {
    ...(previous ? { onPrev: () => onNavigate(previous) } : {}),
    ...(next ? { onNext: () => onNavigate(next) } : {}),
    position: `${String(at + 1)} of ${String(ids.length)}`,
  }
}
